import { useAuthStore } from "@/states";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { type AxiosRequestConfig } from "axios";

/** Priced itinerary summary, the same shape the agent portal assistant shows in its draft panel. */
export type AssistantItinerarySummary = {
  title: string;
  startDate: string;
  endDate: string;
  nights: number;
  destinations: string[];
  travellers: { adults: number; cwb: number; cnb: number; infant: number };
  currency: string;
  totalPrice: number;
  perAdult: number;
  hotels: Array<{
    id: string;
    name: string;
    city: string;
    category: string | null;
    roomType: string;
    mealPlan: string;
    checkInDate: string;
    checkOutDate: string;
    nights: number;
    rooms: number;
  }>;
  days: Array<{
    day: number;
    date: string;
    items: Array<{ id: string; type: "activity" | "transport"; name: string; city: string | null }>;
  }>;
  gaps: {
    daysWithoutItems: number[];
    uncoveredHotelNights: number[];
    missingTransfers?: Array<{ day: number; reason: string }>;
  };
  warnings: string[];
};

export type AssistantActionStatus = "pending" | "approved" | "declined" | "failed" | "expired";

/** A change the assistant prepared that is waiting for (or has received) the user's decision. */
export type AssistantAction = {
  id: string;
  tool: string;
  title: string;
  args: Record<string, unknown>;
  /** Priced preview of what an itinerary change will create. */
  preview?: { guestName: string; summary: AssistantItinerarySummary } | null;
  status: AssistantActionStatus;
  createdAt: string;
  resultSummary: string | null;
};

export type AssistantMessage = {
  role: "user" | "assistant";
  content: string;
  createdAt?: string;
  kind?: "action";
  actionId?: string;
};

export type AssistantThread = {
  id: string;
  title: string | null;
  messages: AssistantMessage[];
  actions: AssistantAction[];
  updatedAt: string;
};

export type AssistantThreadSummary = { id: string; title: string | null; updatedAt: string };

export type AssistantStatus = { available: boolean; reason?: string; suggestions?: string[] };

export type AssistantChatResult = {
  threadId: string;
  title: string | null;
  message: string;
  actions: AssistantAction[];
};

export const assistantKeys = {
  status: ["assistant", "status"] as const,
  threads: () => ["assistant", "threads"] as const,
  thread: (id: string) => ["assistant", "thread", id] as const,
};

/** Reports whether the assistant is enabled for this user's organisation. Drives the navbar entry. */
export const useAssistantStatusQuery = (enabled = true) =>
  useQuery({
    queryKey: assistantKeys.status,
    queryFn: async (): Promise<AssistantStatus> => (await axios.get("/api/assistant/status")).data,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });

/** Lists the signed-in user's earlier assistant chats. */
export const useAssistantThreadsQuery = (enabled: boolean) =>
  useQuery({
    queryKey: assistantKeys.threads(),
    queryFn: async (): Promise<AssistantThreadSummary[]> => (await axios.get("/api/assistant/threads")).data.threads,
    enabled,
    staleTime: 15_000,
  });

/** Deletes one assistant chat. */
export const useDeleteAssistantThreadMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string) => axios.delete(`/api/assistant/threads/${threadId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assistantKeys.threads() }),
  });
};

/** Approves or declines a change the assistant prepared. */
export const useResolveAssistantActionMutation = () =>
  useMutation({
    mutationFn: async (params: { threadId: string; actionId: string; decision: "approve" | "decline" }) =>
      (
        await axios.post(`/api/assistant/actions/${params.actionId}/${params.decision}`, { threadId: params.threadId })
      ).data as { message: AssistantMessage; actions: AssistantAction[]; waiting: number },
  });

export class AssistantChatError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** Refreshes the access token through the refresh cookie; streaming uses fetch, which bypasses the axios interceptor. */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const { data } = await axios.post("/api/auth/refresh", {}, { withCredentials: true, _retry: true, _skipAuthRefresh: true } as AxiosRequestConfig);
    const token = data?.token as string | undefined;
    if (!token) return null;
    useAuthStore.getState().setToken(token);
    return token;
  } catch {
    return null;
  }
}

const openChatStream = (token: string | null, body: string, signal?: AbortSignal) =>
  fetch("/api/assistant/chat", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    signal,
  });

/**
 * Sends one message to the assistant and reports progress while it works.
 * Resolves with the reply and the changes waiting for approval.
 */
export const sendAssistantMessage = async (params: {
  message: string;
  threadId?: string;
  signal?: AbortSignal;
  onStatus?: (step: string) => void;
}): Promise<AssistantChatResult> => {
  const body = JSON.stringify({ message: params.message, threadId: params.threadId });
  const interrupted = () =>
    new AssistantChatError("The connection to the assistant was interrupted. Your message wasn't lost, try again.", 0);

  let response: Response;
  try {
    response = await openChatStream(localStorage.getItem("token"), body, params.signal);
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) response = await openChatStream(refreshed, body, params.signal);
    }
  } catch (error) {
    if (params.signal?.aborted) throw error;
    throw interrupted();
  }
  if (!response.ok || !response.body) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw payload?.error ? new AssistantChatError(payload.error, response.status) : interrupted();
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: AssistantChatResult | null = null;
  for (;;) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch (error) {
      if (params.signal?.aborted) throw error;
      throw interrupted();
    }
    if (chunk.done) break;
    buffer += decoder.decode(chunk.value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() || "";
    for (const raw of events) {
      const line = raw.split("\n").find((entry) => entry.startsWith("data: "));
      if (!line) continue;
      const event = JSON.parse(line.slice(6)) as { type: string; step?: string; error?: string } & Partial<AssistantChatResult>;
      if (event.type === "status" && event.step) params.onStatus?.(event.step);
      if (event.type === "error") throw new AssistantChatError(event.error || "The AI assistant ran into a problem.", 500);
      if (event.type === "complete") result = event as AssistantChatResult;
    }
  }
  if (!result) throw interrupted();
  return result;
};
